import { getTime } from 'date-fns';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { findIndex, isEmpty, isObject, replace } from 'lodash';
import { isUndefined } from 'util';

import {
  clearNonNumberCharacters,
  getEntries,
  isNullOrEmpty,
  mappingErrorValuesForGroup,
  parseFloatCommaDot,
} from '../../libs/helpers/Utils';
import { Errors } from '../../libs/i18n/messages';
import { IControlError, IControlSchema, ValidationResult } from '../Controls';
import { validate } from '../../libs/controls/DynamicGridPanel/validate';
import { Dictionary } from '../../types/Dictionary';
import { ISelectBlockSchema } from '../../libs/controls/SelectBlock';
import { ITextBoxSchema } from '../../libs/controls/TextBox';
import { COUNTRIES_CODE } from '../../libs/constants';
import { getLengthDependence } from '../../libs/controls/Grid/GridEditable/helpers';
import RulesManager from './RulesManager';

interface ErrorResult {
  isError: boolean;
  message: string;
}

export interface ErrorField {
  controlId: string;
  control: any;
  error:
    | {
        message: any;
        isFrontValidation: boolean;
      }
    | undefined;
}

const isInteger = (value: string) =>
  !isNullOrEmpty(value) && /^\d+$/.test(value);

const isCurrency = (value: string) =>
  (!isNullOrEmpty(value) && /^-{1}|\d+\.\d{1,2}$/.test(value)) ||
  /^\d+$/.test(value);

const createObjectColumns = (_columns, groups = []) =>
  _columns?.reduce((acc, column) => {
    if (column.field !== 'key' && column?.required) {
      acc[column.field] = column;
    }
    if (groups?.length) {
      acc.groups = createObjectColumns(groups);
    }
    return acc;
  }, {});

const isMin = (value: string, min: number) =>
  min && parseFloat(replace(value, ',', '.')) < min;

const isMax = (value: string, max: number) =>
  max && parseFloat(replace(value, ',', '.')) > max;

export function validateTelephone(
  value: string | null | undefined,
): ErrorResult {
  const error: ErrorResult = {
    isError: false,
    message: undefined,
  };

  if (value && value !== "+") {
    const ph = parsePhoneNumber(value?.replace(/[^0-9|+]/gi, ''));

    if (
      COUNTRIES_CODE.includes(ph.getRegionCode()?.toLowerCase()) &&
      ph.isValid()
    ) {
      error.message = undefined;
    } else {
      error.message = 'Введён некорректный телефонный номер';
    }
  }
  error.isError = !isUndefined(error.message);
  return error;
}

/** Проверка ИНН юрлиц, физлиц и ИП у
 * @param {string} value - строка для проверки
 * @return string | undefined
 */
function validateInn(value: string, type?: 'individual' | 'juridical') {
  const defaultErr = 'Неверный ИНН';

  //ИНН юрлиц - 10 символов, ИП - 12 символов
  if (type === 'individual' && value.length !== 12) {
    return `Некорректная длина ИНН: ${value.length}. Длина ИНН физ.лиц - 12 символов`;
  }

  if (type === 'juridical' && value.length !== 10) {
    return `Некорректная длина ИНН: ${value.length}. Длина ИНН юр.лиц - 10 символов`;
  }

  if (value.length !== 10 && value.length !== 12) {
    return `Некорректная длина ИНН: ${value.length}. Длина ИНН юр.лиц - 10 символов, физ.лиц - 12 символов`;
  }

  // неверные значения, проходящие проверку контрольной суммы
  if (value === '0000000000' || value === '000000000000') return defaultErr;

  //преобразуем строку в массив цифр
  const arNumbers = value.split('').map((el) => Number(el));

  //проверим что у нас в массиве только цифры
  for (let number of arNumbers) {
    if (isNaN(number)) {
      return `ИНН может состоять только из цифр`;
    }
  }

  //формула для юрлиц и ИП отличается
  if (arNumbers.length === 10) {
    //переменная для итоговой суммы
    let checkSum;

    //каждую цифру мы умножаем на свой коэффициент
    //а потом получаем остаток от деления на 11 и на 10
    checkSum =
      ((2 * arNumbers[0] +
        4 * arNumbers[1] +
        10 * arNumbers[2] +
        3 * arNumbers[3] +
        5 * arNumbers[4] +
        9 * arNumbers[5] +
        4 * arNumbers[6] +
        6 * arNumbers[7] +
        8 * arNumbers[8]) %
        11) %
      10;

    //проверяем что десятый символ ИНН совпадает с контрольной суммой

    // Контрольная сумма не совпала с десятым символом ${checkSum} != ${arNumbers[9]}
    if (checkSum !== Number(arNumbers[9])) return defaultErr;

    return;
  }

  //код для ИП
  if (arNumbers.length === 12) {
    //в этом случае будет две контрольные суммы
    let checkSumOne, checkSumTwo;

    checkSumOne =
      ((7 * arNumbers[0] +
        2 * arNumbers[1] +
        4 * arNumbers[2] +
        10 * arNumbers[3] +
        3 * arNumbers[4] +
        5 * arNumbers[5] +
        9 * arNumbers[6] +
        4 * arNumbers[7] +
        6 * arNumbers[8] +
        8 * arNumbers[9]) %
        11) %
      10;

    checkSumTwo =
      ((3 * arNumbers[0] +
        7 * arNumbers[1] +
        2 * arNumbers[2] +
        4 * arNumbers[3] +
        10 * arNumbers[4] +
        3 * arNumbers[5] +
        5 * arNumbers[6] +
        9 * arNumbers[7] +
        4 * arNumbers[8] +
        6 * arNumbers[9] +
        8 * arNumbers[10]) %
        11) %
      10;

    // Первая контрольная сумма не совпала
    // с одиннадцатым символом ${checkSumOne} != ${arNumbers[10]},
    if (checkSumOne !== Number(arNumbers[10])) return defaultErr;

    // Вторая контрольная сумма не совпала
    // с двенадцатым символом ${checkSumTwo} != ${arNumbers[11]}
    if (checkSumTwo !== Number(arNumbers[11])) return defaultErr;

    return;
  }
}

function validateSnils(value: string) {
  const checkSum = parseInt(value.slice(9), 10);

  if (value.length !== 11) {
    return `Некорректная длина СНИЛС: ${value.length}.`;
  }

  const valueToArray = value.split('');
   
  // @ts-ignore
  const sum = (valueToArray[0] * 9 + valueToArray[1]*8 + valueToArray[2]*7 + valueToArray[3]*6 + valueToArray[4]*5 + valueToArray[5]*4 + valueToArray[6]*3 + valueToArray[7]*2 + valueToArray[8]*1);
   
  if (sum < 100 && sum == checkSum) {
    return;
  } 
  else if ((sum == 100 || sum == 101) && checkSum == 0) {
    return;
  } 
  else if (sum > 101 && (sum%101 == checkSum || (sum%101 == 100 && checkSum == 0))) {
    return;
  }
  else {
    return "Некорректный СНИЛС";
  }
}

export function TextBoxValidate(
  value: string,
  schema: ITextBoxSchema,
): ErrorResult {
  const error: ErrorResult = {
    isError: false,
    message: undefined,
  };
  const type: string = schema.type?.toUpperCase();
  const contentType: string =
    schema.extra &&
    schema.extra.valueType &&
    schema.extra.valueType.toUpperCase();

  if (value && !schema.disabled) {
    switch (contentType || type) {
      case 'NUMBER':
        if (schema.isInteger && !isInteger(value)) {
          error.message = 'Неверный формат поля';
        }
        if (!schema.isInteger && !isCurrency(value)) {
          error.message = 'Неверный формат поля';
        } else if (isMin(value, schema.min)) {
          error.message = `${Errors.MIN_VALUE.ru}: ${schema.min}`;
        } else if (isMax(value, schema.max)) {
          error.message = `${Errors.MAX_VALUE.ru}: ${schema.max}`;
        } else if (String(value).length < schema.minLength) {
          error.message = `${Errors.MIN_LENGTH.ru}: ${schema.minLength}`;
        } else if (String(value).length > schema.maxLength) {
          error.message = `${Errors.MAX_LENGTH.ru}: ${schema.maxLength}`;
        }
        break;
      case 'LINK':
        if (
          !/((ftp|https|http?):\/\/)?(www\.)?[A-Za-z0-9$_.+!*'(),-]+\.[A-Za-z0-9$_.+!*'(),//-]+/.test(
            value,
          )
        ) {
          error.message = 'Неверный url';
        }
        break;
      case 'SITE':
        if (
          !/^(ftp|http|https):\/\/[^ "]+$/.test(value) &&
          !/^[A-Za-z0-9А-Яа-я-]+\.[A-Za-z0-9А-Яа-я-.]+$/.test(value)
        ) {
          error.message = Errors.NOT_LINK.ru;
        }
        break;
      case 'EMAIL':
        if (
          // eslint-disable-next-line max-len
          !/^(|(([A-Za-z0-9А-Яа-я]+_+)|([A-Za-z0-9]+-+)|([A-Za-z0-9А-Яа-я]+\.+)|([A-Za-z0-9А-Яа-я]+\++))*[A-Za-z0-9А-Яа-я]+@((\w+-+)|(\w+\.))*\w{1,63}\.[a-zA-ZА-Яа-я]{2,20})$/i.test(
            value,
          )
        ) {
          error.message = Errors.NOT_EMAIL.ru;
        }
        break;
      case 'TELEPHONE':
      case 'PHONE':
        const errorPhone = validateTelephone(value);
        if (!!errorPhone?.message) {
          // error.message = errorPhone.message;
          error.message = Errors.WRONG_PHONE.ru;
        }
        break;
      case 'INN':
        error.message = validateInn(value);
        break;
      case 'INN_INDIVIDUAL':
        error.message = validateInn(value, 'individual');
        break;
      case 'INN_JURIDICAL':
        error.message = validateInn(value, 'juridical');
        break;
      case 'SNILS_INDIVIDUAL': 
        error.message = validateSnils(value);
        break;
      default:
        if (value.length > schema.maxLength) {
          error.message = `${Errors.MAX_LENGTH.ru} : ${schema.maxLength}`;
        }
        if (value.length < schema.minLength) {
          error.message = `${Errors.MIN_LENGTH.ru} : ${schema.minLength}`;
        }
        break;
    }
  }
  error.isError = !isUndefined(error.message);
  return error;
}

export function dateFormat(value) {
  if (value.length > 7) {
    return clearNonNumberCharacters(value.substring(0, 7)).replace(
      /^\D|\D$/g,
      '',
    );
  }

  switch (value.length) {
    case 1:
      if (value[0] > 1) {
        value = '1';
      }
      break;
    case 2:
      if (value.substr(0, 2) > 12) {
        value = '12';
      }
      break;
    case 3:
    case 4:
      if (value[2] !== '.') {
        value = `${value.substr(0, 2)}.${value[2]}`;
      }
      if (value[3] < 2) {
        value = `${value.substr(0, 3)}2`;
      }
      break;
    default:
      break;
  }

  return clearNonNumberCharacters(value).replace(/^\D|\D$/g, '');
}

export function phoneFix(value) {
  if (value.length > 11) {
    return value.slice(0, 12);
  }
  if (value.length === 1) {
    if (!value.match(/^[0-9+]/)) {
      value = '';
    } else if (value === '8') {
      value = '+7';
    } else if (value === '+') {
      return value;
    } else {
      value = `+7${value}`;
    }
  } else {
    if (value === '') {
      return value;
    }
    if (
      (value.match(/^[0-9]*$/) && value.substr(0, 1) === '8') ||
      value.substr(0, 2) !== '+7'
    ) {
      value = `+7${value.substr(1).replace(/[^0-9]/, '')}`;
    } else if (value.match(/^[0-9]*$/)) {
      value = `+${value.replace(/[^0-9]/, '')}`;
    } else {
      value = `+${value.substr(1).replace(/[^0-9]/, '')}`;
    }
  }
  return value;
}

export function SelectBlockValidate(
  value: any,
  schema: ISelectBlockSchema,
  opts: any,
  external_tags?: any,
): ErrorResult {
  const error: ErrorResult = {
    isError: false,
    message: undefined,
  };
  const currVal = typeof value === 'string' ? JSON.parse(value) : value;

  let elements;
  if (external_tags) {
    elements = external_tags;
  } else {
    switch (schema.extra.init) {
      case 'getTags':
        elements = opts.tags;
        break;
      case 'getCities':
        elements = opts.cities;
        break;
      case 'getPeople':
        elements = opts.people;
        break;
      default:
        break;
    }
  }

  if (schema.required && value && !schema.disabled) {
    if (currVal.length < schema.minSelected) {
      error.message = `${Errors.MIN_SELECTED_ITEMS.ru}: ${schema.minSelected}`;
    } else if (currVal.length > schema.maxSelected) {
      error.message = `${Errors.MAX_SELECTED_ITEMS.ru}: ${schema.maxSelected}`;
    }
  }

  if (!external_tags) {
    currVal.forEach((item) => {
      const tag = elements.find((el) => el.id === item);
      if (tag?.type_id !== schema?.extra?.rootTagId && tag?.type_id !== null) {
        error.message = Errors.WRONG_TAG_SELECTED.ru;
      }
    });
  }

  error.isError = !isUndefined(error.message);
  return error;
}

export function CheckboxSetBlockValidate(
  value: any,
  schema: ISelectBlockSchema,
): ErrorResult {
  const error: ErrorResult = {
    isError: false,
    message: undefined,
  };
  if (schema.required && value && !schema.disabled) {
    const valLength = value.length || 0;
    if (valLength < schema.minSelected) {
      error.message = `${Errors.MIN_SELECTED_ITEMS.ru}: ${schema.minSelected}`;
    } else if (valLength > schema.maxSelected) {
      error.message = `${Errors.MAX_SELECTED_ITEMS.ru}: ${schema.maxSelected}`;
    }
  } else if (schema.required && (!value || value.length === 0)) {
    error.message = Errors.REQUIRED.ru;
  }
  error.isError = !isUndefined(error.message);
  return error;
}

interface IValueCheked {
  value: {
    checked: boolean;
  };
}

export function isGroupCheck(ctrl: any, objValue: any): boolean {
  if (ctrl.type?.toUpperCase() === 'GROUP') {
    if (isUndefined(ctrl.visible)) return true;
    return ctrl.visible;
  }
  if (ctrl.type?.toUpperCase() === 'PANEL') {
    if (isUndefined(ctrl.visible) && ctrl.required) return true;
    if (ctrl.visible && ctrl.required) return true;

    if (!isUndefined(ctrl.visible) && !ctrl.required) {
      if (ctrl.visible && isObject(objValue)) {
        return ctrl.visible && (objValue as IValueCheked).value.checked;
      }
      if (ctrl.visible && isUndefined(objValue)) {
        return false;
      }
    } else if (isObject(objValue)) {
      return (objValue as IValueCheked).value.checked;
    }
  }

  return false;
}

export function* onValidate(
  controls: Array<IControlSchema>,
  values: Array<any>,
  form: any,
  validateEmptyRequired: boolean,
  errors: Dictionary<IControlError | undefined>,
): Iterable<ErrorField> {
  //Делаем так чтоб валидация проходила только на нужных ctrl. Потому что если в группе будет 5 спрятанных полей, он выдаст ошибку, но не покажет где она
  const rulesManager = new RulesManager();
  const clearedCtrls = rulesManager.round(controls, values);

  // eslint-disable-next-line no-restricted-syntax
  for (const ctrl of clearedCtrls) {
    yield* validateControl(ctrl, values, form, validateEmptyRequired, errors);
  }
}

const controlsWithCommonValidationOnly = {
  OBJECT: true,
  SERVICETABLE: true,
  LABEL: true,
  GROUP: true,
  PANEL: true,
  DYNAMICGRIDPANEL: true,
  TOTALHEADER: true,
};

export const isNotEmpty = (value) =>
  typeof value !== 'undefined' &&
  !!value &&
  value !== '' &&
  value !== ' ' &&
  value !== 'false' &&
  value !== 'undefined' &&
  JSON.stringify(value) !== '[]' &&
  value !== '[]';

export function* validateControl(
  ctrl: IControlSchema,
  values: Array<any>,
  form: any,
  validateEmptyRequired: boolean,
  errors: Dictionary<IControlError | undefined>,
  opts?: any,
): Iterable<ErrorField> {
  const valueIdx = findIndex(values, (val: any) => val.controlid === ctrl.id);
  const error = typeof errors !== 'undefined' ? errors[ctrl.id] : undefined;

  if (error && !controlsWithCommonValidationOnly[ctrl.type?.toUpperCase()]) {
    yield {
      controlId: ctrl.id,
      control: ctrl,
      error: {
        message: error.message,
        isFrontValidation: error.isFrontValidation,
      },
    };
    return;
  }

  if (
    !Object.prototype.hasOwnProperty.call(ctrl, 'controls') &&
    (isUndefined(ctrl.visible) || ctrl.visible)
  ) {
    const idx = findIndex(
      values,
      (val: any) => val.controlid === ctrl.id && isNotEmpty(val.value),
    );

    if (ctrl?.type?.toUpperCase() === 'GROUP' && ctrl?.required) {
      const message = {};

      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const groupID in ctrl.value) {
        const mappedValues = Object.entries(ctrl.value[groupID]).map(
          ([controlid, value]) => ({
            controlid,
            value,
          }),
        );

        const errorControls = Array.from(
          onValidate(ctrl.columns, mappedValues, form, true, undefined),
        ).filter((_ctrl) => _ctrl.error);

        if (!isEmpty(errorControls)) {
          message[groupID] = mappingErrorValuesForGroup(errorControls);
        }
      }

      yield {
        controlId: ctrl.id,
        control: ctrl,
        error: !isEmpty(message)
          ? {
              message,
              isFrontValidation: true,
            }
          : undefined,
      } as any;
      return;
    }

    if (idx === -1 && ctrl.required && ctrl.type?.toUpperCase()) {
      let message = '';
      switch (ctrl.type?.toUpperCase()) {
        case 'CHECKBOX':
          message = 'Не принято обязательное соглашение';
          break;
        case 'GRID':
        case 'GRIDEDITABLE':
          message = 'Таблица должна содержать хотя бы одну запись';
          break;
        case 'RADIOBUTTON':
          message = 'Не выбран, ни один из пунктов';
          break;
        default:
          message = 'Не заполнено обязательное поле';
          break;
      }
      if (validateEmptyRequired && ctrl.type?.toUpperCase()) {
        yield {
          controlId: ctrl.id,
          control: ctrl,
          error: {
            message,
            isFrontValidation: true,
          },
        };
      } else {
        yield {
          controlId: ctrl.id,
          control: ctrl,
          error: undefined,
        };
      }
      return;
    }
    if (idx !== -1) {
      switch (ctrl.type?.toUpperCase()) {
        case 'TEXTBOX':
        case 'TEXTAREA':
        case 'PHONE':
        case 'EMAIL':
        case 'LINK':
        case 'SITE':
        case 'NUMBER':
        case 'INN':
        case 'INN_JURIDICAL':
        case 'INN_INDIVIDUAL': {
          // TODO: no-shadow - global variable error === local variable
          const _error = TextBoxValidate(
            values[idx].value,
            ctrl as ITextBoxSchema,
          );
          if (_error.isError) {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message: _error.message,
                isFrontValidation: true,
              },
            };
            return;
          }
          break;
        }
        case 'SELECTBLOCK': {
          if (opts) {
            const _error = SelectBlockValidate(
              values[idx].value,
              ctrl as ISelectBlockSchema,
              opts,
            );
            if (_error.isError) {
              yield {
                controlId: ctrl.id,
                control: ctrl,
                error: {
                  message: _error.message,
                  isFrontValidation: true,
                },
              };
              return;
            }
          }
          break;
        }
        case 'FLOAT': {
          const { value } = values[idx];
          if (value !== '') {
            if (Number.isNaN(parseFloatCommaDot(value))) {
              yield {
                controlId: ctrl.id,
                control: ctrl,
                error: {
                  message: 'Введите число',
                  isFrontValidation: true,
                },
              };
              return;
            }
          }
          break;
        }
        case 'CALENDAR': {
          const { value } = values[idx];

          // TODOS: no-shadow - возможно надо будет отключить
          // eslint-disable-next-line no-shadow
          const validateCalendar = (value, ctrl, validateEmptyRequired) => {
            const startDateUnix = getTime(new Date(value?.beginDate));
            const endDateUnix = getTime(new Date(value?.finishDate));

            if (typeof value === 'string' && !value.trim()) {
              return {
                controlId: ctrl.id,
                control: ctrl,
                error: {
                  message: 'Не заполнено обязательное поле',
                  isFrontValidation: true,
                },
              };
            }
            if (
              (isEmpty(value?.beginDate) || isEmpty(value?.finishDate)) &&
              typeof value === 'object'
            ) {
              if (validateEmptyRequired) {
                return {
                  controlId: ctrl.id,
                  control: ctrl,
                  error: {
                    message: 'Не заполнено обязательное поле',
                    isFrontValidation: true,
                  },
                };
              }
            }
            if (startDateUnix > endDateUnix && typeof value === 'object') {
              if (validateEmptyRequired) {
                return {
                  controlId: ctrl.id,
                  control: ctrl,
                  error: {
                    message: 'Поле "ОТ" должно быть меньше поля "ДО"',
                    isFrontValidation: true,
                  },
                };
              }
            }
            return {
              controlId: ctrl.id,
              control: ctrl,
              error: undefined,
            };
          };

          if (value?.length) {
            let validation;
            // eslint-disable-next-line no-restricted-syntax
            for (const item of value) {
              validation = validateCalendar(item, ctrl, validateEmptyRequired);
              if (validation.error) {
                break;
              }
            }
            yield validation;
          } else {
            yield validateCalendar(value, ctrl, validateEmptyRequired);
          }
          break;
        }
        case 'GRIDEDITABLE': {
          const { columns, template, extra } = ctrl;
          const validateErrors = [];
          const validateWithRules = [];
          const dependenceControl = getLengthDependence(
            values,
            extra?.lengthFromId,
          );

          if (extra?.lengthFromId && template?.columns) {
            if (
              dependenceControl !==
              values?.[valueIdx]?.value?.[0]?.groups?.length
            ) {
              yield {
                controlId: ctrl.id,
                control: ctrl,
                error: {
                  message: 'Заполните все этапы',
                  isFrontValidation: true,
                },
              };

              return;
            }
          }

          const { groups: _groupColumns, ..._columns } = createObjectColumns(
            columns,
            template?.columns,
          );

          // TODO: да простите меня =)
          for (const value of values?.[valueIdx]?.value) {
            const { groups, ...otherValue } = value;

            getEntries(otherValue).forEach(([keyCol, valueCol]) => {
              // TODO: потом доделать - а пока костыль!!!
              if (keyCol !== 'key') {
                if (!valueCol && _columns?.[keyCol]) {
                  validateErrors.push('Заполните обязательные поля');
                }
              }
            });

            if (groups?.length) {
              groups?.forEach((group) => {
                getEntries(group)?.forEach(([groupKey, groupValue]) => {
                  if (groupKey !== 'key') {
                    if (
                      Boolean(groupValue) === false &&
                      _groupColumns?.[groupKey]?.required
                    ) {
                      validateErrors.push('Заполните обязательные поля');
                    }

                    // TODO: ПОКА КОСТЫЛЬ -> потом переделать
                    // нет ничего более временного чем постоянное
                    if (groupValue && _groupColumns?.[groupKey]?.rules) {
                      const { maxCount } = _groupColumns?.[groupKey].rules;
                      if (+groupValue > maxCount.value) {
                        validateWithRules.push(maxCount.error);
                      }
                    }
                  }
                });
              });
            }
          }

          if (validateErrors?.length || validateWithRules?.length) {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message: validateWithRules?.[0] ?? validateErrors?.[0] ?? '',
                isFrontValidation: true,
              },
            };

            return;
          }

          return;
        }
        case 'GRID': {
          let gridEmpty = false;
          const rowWhoHaveError = [];
          // for (let column of ctrl.columns) {
          // eslint-disable-next-line no-restricted-syntax

          for (const column of values[valueIdx].value) {
            // TODO: NE RABOTAET ili tut mega mutation Value  !!!
            if (column.required) {
              // eslint-disable-next-line no-loop-func
              values[idx].value.forEach((val, index) => {
                if (
                  typeof val[column.field] === 'undefined' ||
                  isNullOrEmpty(val[column.field])
                ) {
                  gridEmpty = true;
                  rowWhoHaveError.push(index);
                }
              });
            }
          }

          if (gridEmpty) {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message: rowWhoHaveError.map((item) => ({
                  message: 'Не все обязательные поля таблицы заполнены',
                  index: item,
                })),
                isFrontValidation: true,
              },
            };
          } else if (
            ctrl.minLength > values[idx].value.length ||
            ctrl.maxLength < values[idx].value.length
          ) {
            let message;

            if (values[idx].value.length > ctrl.maxLength) {
              message = `Всего добавлено: ${values[idx].value.length} строк ${
                ctrl.maxLength
                  ? `(Превышено на: ${
                      values[idx].value.length - ctrl.maxLength
                    })`
                  : ''
              }`;
            } else {
              message = `Всего добавлено: ${values[idx].value.length} строк ${
                ctrl.minLength
                  ? `(Не хватает: ${ctrl.minLength - values[idx].value.length})`
                  : ''
              }`;
            }

            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message,
                isFrontValidation: true,
              },
            };
          } else {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: undefined,
            };
          }
          return;
        }
        default:
          return;
      }
    } else if (
      [
        'EquipmentRentPrepayment',
        'TestingPrepaid',
        'CreatePrototypesPrepaid',
      ].concat(form) &&
      ctrl.id === 'AffiliateFlag'
    ) {
      const vl = values[idx].value;
      if (vl === 'Подрядчик аффилирован') {
        switch (form) {
          case 'EquipmentRentPrepayment': {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message:
                  'Услуги аффилированного ЦКП могут быть только компенсированы. Необходимо заполнить заявку вида Аренда оборудования (Компенсация)',
                isFrontValidation: true,
              },
            };
            return;
          }
          case 'TestingPrepaid': {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message:
                  'Услуги аффилированного ЦКП могут быть только компенсированы. Необходимо заполнить заявку вида Проведение испытаний (Компенсация)',
                isFrontValidation: true,
              },
            };
            return;
          }
          case 'CreatePrototypesPrepaid': {
            yield {
              controlId: ctrl.id,
              control: ctrl,
              error: {
                message:
                  'Услуги аффилированного ЦКП могут быть только компенсированы. Необходимо заполнить заявку вида Создание прототипов (Компенсация)',
                isFrontValidation: true,
              },
            };
            return;
          }
          default:
            return;
        }
      }
    }
  } else if (isGroupCheck(ctrl, values[valueIdx])) {
    if (
      ctrl.type?.toUpperCase() === 'PANEL' &&
      valueIdx > -1 &&
      !isEmpty(values[valueIdx].value.rows)
    ) {
      const { rows } = values[valueIdx].value;
      // eslint-disable-next-line no-restricted-syntax
      for (const row of rows) {
        // eslint-disable-next-line no-restricted-syntax
        for (const controlRow of row.controls) {
          const index = findIndex(
            values,
            (val: any) => val.controlid === controlRow.id,
          );
          const idxCtrl = findIndex(
            ctrl.controls,
            (obj: any) => obj.id === controlRow.name,
          );
          const childControl = ctrl.controls[idxCtrl];

          if (index === -1 && childControl.required) {
            let message = '';
            switch (ctrl.type?.toUpperCase()) {
              case 'CHECKBOX':
                message = 'Не принято обязательное соглашение';
                break;
              case 'GRID':
              case 'GRIDEDITABLE':
                message = 'Таблица должна содержать хотя бы одну запись';
                break;
              case 'RADIOBUTTON':
                message = 'Не выбран, не одни из пунктов ';
                break;
              default:
                message = 'Не заполнено обязательное поле';
                break;
            }

            if (validateEmptyRequired) {
              yield {
                controlId: controlRow.id,
                control: ctrl,
                error: {
                  message,
                  isFrontValidation: true,
                },
              };
            } else {
              yield {
                controlId: controlRow.id,
                control: ctrl,
                error: undefined,
              };
            }
          } else if (index !== -1) {
            switch (ctrl.type?.toUpperCase()) {
              case 'TEXTBOX':
              case 'PHONE':
              case 'EMAIL':
              case 'TEXTAREA':
              case 'NUMBER':
              case 'LINK':
              case 'SITE':
                const _error = TextBoxValidate(
                  values[index].value,
                  ctrl as ITextBoxSchema,
                );
                if (_error.isError) {
                  yield {
                    controlId: ctrl.id,
                    control: ctrl,
                    error: {
                      message: _error.message,
                      isFrontValidation: true,
                    },
                  };
                }
                break;
              default:
                break;
            }
          }
        }
      }
    }

    const _error = onValidate(
      ctrl.controls,
      values,
      form,
      validateEmptyRequired,
      errors,
    );
    if (_error !== null) {
      yield* _error;
      return;
    }
  } else if (ctrl.type?.toUpperCase() === 'DYNAMICGRIDPANEL') {
    if (valueIdx === -1) {
      if (validateEmptyRequired) {
        yield {
          controlId: ctrl.id,
          control: ctrl,
          error: {
            message: 'Не заполненен обязательный раздел',
            isFrontValidation: true,
          },
        };
      } else {
        yield {
          controlId: ctrl.id,
          control: ctrl,
          error: undefined,
        };
      }
      return;
    }
    const state = values[valueIdx].value;
    yield* validate(state, values, validateEmptyRequired);
  }
  yield {
    controlId: ctrl.id,
    control: ctrl,
    error: undefined,
  };
}
